import { useMemo, useState } from 'react';
import type { Dimension, DrillDownRow } from '../types';
import { formatDimensionValue, formatCompactCurrency } from '../utils';
import { FilterChips } from './FilterChips';

interface SmartFilterProps {
  dimensions: Dimension[];
  data: DrillDownRow[];
  activeFilters: Record<string, string[]>;
  onFilterChange: (key: string, values: string[]) => void;
  onClearAll: () => void;
  searchQuery?: string;
  onSearchChange?: (query: string) => void;
}

export function SmartFilter({ dimensions, data, activeFilters, onFilterChange, onClearAll, searchQuery: externalSearchQuery, onSearchChange }: SmartFilterProps): JSX.Element {
  const [expandedFilter, setExpandedFilter] = useState<string | null>(null);
  const [internalSearchQuery, setInternalSearchQuery] = useState('');

  // Use external search query if provided, otherwise use internal state
  const searchQuery = externalSearchQuery ?? internalSearchQuery;

  const handleSearchChange = (value: string) => {
    if (onSearchChange) {
      onSearchChange(value);
    } else {
      setInternalSearchQuery(value);
    }
  };

  // Calculate top 50 values by total amount for each dimension
  const topValuesByDimension = useMemo(() => {
    const result: Record<string, Array<{ value: string; total: number; count: number }>> = {};

    dimensions.slice(0, 5).forEach((dim) => {
      const valueTotals = new Map<string, { total: number; count: number }>();

      data.forEach((row) => {
        let value: string;
        if (dim.key === '_month' && row.Data) {
          const date = new Date(row.Data);
          value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        } else if (dim.key === '_day' && row.Data) {
          const date = new Date(row.Data);
          value = date.toISOString().split('T')[0];
        } else {
          const rawValue = row[dim.key];
          value = rawValue != null && String(rawValue).trim() !== '' ? String(rawValue).trim() : '';
        }

        if (value) {
          const existing = valueTotals.get(value) || { total: 0, count: 0 };
          existing.total += Number(row.Valor) || 0;
          existing.count += 1;
          valueTotals.set(value, existing);
        }
      });

      result[dim.key] = Array.from(valueTotals.entries())
        .map(([dimValue, stats]) => ({ value: dimValue, ...stats }))
        .sort((a, b) => Math.abs(b.total) - Math.abs(a.total))
        .slice(0, 50);
    });

    return result;
  }, [dimensions, data]);

  return (
    <div className="mb-4">
      {/* Filter Bar */}
      <div className="flex gap-2 items-center flex-wrap">
        {/* Search */}
        <input
          type="text"
          placeholder="Buscar..."
          value={searchQuery}
          onChange={(e) => handleSearchChange(e.target.value)}
          className="smart-filter-search py-2 px-3 rounded-lg border border-brand-dark-lighter bg-brand-dark-lighter text-white w-[180px] font-body focus:ring-2 focus:ring-ring focus:outline-none"
        />

        {/* Quick Filter Dropdowns */}
        {dimensions.slice(0, 3).map((dim) => {
          const topValues = topValuesByDimension[dim.key] || [];
          const isOpen = expandedFilter === dim.key;
          const selectedCount = (activeFilters[dim.key] || []).length;

          return (
            <div key={dim.key} className="relative">
              <button
                onClick={() => setExpandedFilter(isOpen ? null : dim.key)}
                className={`
                  filter-dropdown-button flex items-center gap-2 py-2 px-3 rounded-lg cursor-pointer font-display transition-all duration-150
                  ${selectedCount > 0
                    ? 'border-2 border-brand-primary bg-brand-primary/20 text-brand-primary'
                    : 'border border-brand-dark-lighter bg-brand-dark-lighter text-brand-gray hover:bg-brand-dark hover:text-white'
                  }
                `}
              >
                {dim.label}
                {selectedCount > 0 && (
                  <span className="text-[0.7rem] py-0.5 px-1.5 rounded-full bg-brand-primary text-brand-dark">
                    {selectedCount}
                  </span>
                )}
                <span className="text-xs">{isOpen ? '▲' : '▼'}</span>
              </button>

              {/* Dropdown */}
              {isOpen && (() => {
                const filteredValues = topValues.filter((item) => {
                  if (!searchQuery) return true;
                  const displayValue = formatDimensionValue(item.value, dim.key);
                  return displayValue.toLowerCase().includes(searchQuery.toLowerCase());
                });

                return (
                  <div className="absolute top-full left-0 mt-1 bg-brand-dark-light border border-brand-dark-lighter rounded-lg shadow-monumenta-lg z-[1000] min-w-[250px] max-h-[300px] overflow-y-auto p-2">
                    {filteredValues.map((item) => {
                      const isSelected = (activeFilters[dim.key] || []).includes(item.value);
                      const displayValue = formatDimensionValue(item.value, dim.key);

                      return (
                        <div
                          key={item.value}
                          onClick={() => {
                            const current = activeFilters[dim.key] || [];
                            const newValues = isSelected
                              ? current.filter(v => v !== item.value)
                              : [...current, item.value];
                            onFilterChange(dim.key, newValues);
                          }}
                          className={`
                            flex justify-between items-center p-2 rounded cursor-pointer transition-colors duration-150
                            ${isSelected ? 'bg-brand-primary/20' : 'hover:bg-brand-dark-lighter'}
                          `}
                        >
                          <div className="flex items-center gap-2">
                            <span className={`
                              w-4 h-4 rounded flex items-center justify-center text-[0.7rem]
                              ${isSelected
                                ? 'border-2 border-brand-primary bg-brand-primary text-brand-dark'
                                : 'border-2 border-brand-dark-lighter bg-transparent'
                              }
                            `}>
                              {isSelected && ''}
                            </span>
                            <span className="text-sm text-white max-w-[150px] overflow-hidden text-ellipsis whitespace-nowrap font-body">
                              {displayValue}
                            </span>
                          </div>
                          <span className="text-xs text-brand-gray font-body">
                            {formatCompactCurrency(item.total)}
                          </span>
                        </div>
                      );
                    })}
                    {filteredValues.length === 0 && (
                      <div className="p-4 text-center text-brand-gray font-body">
                        {searchQuery ? 'Nenhum resultado' : 'Sem dados'}
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          );
        })}

      </div>

      {/* Active Filter Chips - delegated to FilterChips component */}
      <FilterChips
        activeFilters={activeFilters}
        dimensions={dimensions}
        onFilterChange={onFilterChange}
        onClearAll={onClearAll}
      />
    </div>
  );
}
